import { Injectable } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';

const base64urlFromBuffer = (buf: ArrayBuffer): string =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

const base64urlFromString = (str: string): string =>
  btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1: string) => String.fromCharCode(parseInt(p1, 16))))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

interface DpopKeyPair {
  privateKey: CryptoKey;
  publicKeyJwk: JsonWebKey & { kid: string };
}

@Injectable({
  providedIn: 'root',
})
export class DpopService {
  private readonly keyPairPromise: Promise<DpopKeyPair>;

  constructor() {
    this.keyPairPromise = this.generateKeyPair();
  }

  private async generateKeyPair(): Promise<DpopKeyPair> {
    const kp = (await crypto.subtle.generateKey(
      { name: 'ECDSA', namedCurve: 'P-256' },
      true,
      ['sign', 'verify']
    )) as CryptoKeyPair;

    const publicKeyJwk = await crypto.subtle.exportKey('jwk', kp.publicKey);
    const kid = uuidv4();

    return {
      privateKey: kp.privateKey,
      publicKeyJwk: { ...publicKeyJwk, kid } as JsonWebKey & { kid: string },
    };
  }

  /**
   * Create a DPoP proof JWT.
   * @param htm  HTTP method (e.g. 'POST', 'GET')
   * @param htu  Full request URL — must match exactly
   * @param accessToken  Provide only for resource requests (adds `ath` claim).
   *                     Omit for the token endpoint.
   */
  async createProof(htm: string, htu: string, accessToken?: string): Promise<string> {
    const { privateKey, publicKeyJwk } = await this.keyPairPromise;

    const header = {
      typ: 'dpop+jwt',
      alg: 'ES256',
      jwk: {
        kty: publicKeyJwk.kty,
        use: 'sig',
        crv: publicKeyJwk.crv,
        kid: publicKeyJwk.kid,
        x: publicKeyJwk.x,
        y: publicKeyJwk.y,
        alg: 'ES256',
      },
    };

    const payload: Record<string, unknown> = {
      htm: htm.toUpperCase(),
      htu,
      iat: Math.floor(Date.now() / 1000),
      jti: uuidv4(),
    };

    if (accessToken) {
      payload['ath'] = await this.computeAth(accessToken);
    }

    const headerB64 = base64urlFromString(JSON.stringify(header));
    const payloadB64 = base64urlFromString(JSON.stringify(payload));
    const signingInput = `${headerB64}.${payloadB64}`;

    const sig = await crypto.subtle.sign(
      { name: 'ECDSA', hash: { name: 'SHA-256' } },
      privateKey,
      new TextEncoder().encode(signingInput)
    );

    return `${signingInput}.${base64urlFromBuffer(sig)}`;
  }

  private async computeAth(accessToken: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(accessToken));
    return base64urlFromBuffer(digest);
  }
}
